// =============================================================================
// The daily checklist (FOH 67) — read side.
//
// The four-sweep checklist the tills run: sections of tasks, ticked four times a
// day (Alpha, Giga, Galactic, Sigma). Today every tablet reads it from Apps
// Script off the Google Sheet. This reads the same thing from Supabase, in
// exactly the same shape, so nothing downstream changes.
//
// GATED OFF. CHECKLIST_READ_HOSTS is empty, so every tablet still reads the
// sheet. Flipping a shop on is a reviewed one-line host add; reverting is
// deleting that line. On a flipped shop the sheet is still the safety net — any
// failure at all falls straight back to it.
//
// Why an EMPTY config counts as a failure: with no sections and no tasks the
// checklist screen renders blank, and staff would think the checklist had been
// switched off. That's a fault, not a valid state, so it falls back to the sheet.
// A day with no events, on the other hand, is perfectly normal — that's a shop
// that hasn't started its first sweep yet — so an empty day is NOT a failure.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { ensureAnonSession } from "./auth.js";
import { fetchJsonWithRetry } from "./backendJson.js";
import { configAuthoritativeEnabled } from "./configAuthoritative.js";

// The sheet read RETRIES until it gets real JSON: the Apps Script /exec, under
// load, hands back an HTML error page with HTTP 200 instead of JSON, so a single
// r.json() throws and the read silently fails (the checklist reads as empty, or a
// just-written tick can't be confirmed — the Meadowhall 2–3 Aug failure). The
// retry lives in the shared backendJson helper; this thin wrapper keeps the
// injected `httpGet` seam the checklist tests use.
function fetchSheetJson(httpGet, url, opts = {}) {
  return fetchJsonWithRetry(url, { ...opts, fetchImpl: httpGet });
}

// --- the gate ---------------------------------------------------------------
// EMPTY on purpose. Add a hostname here to flip that tablet, e.g.
//   "trafford.eatrolled.com",
const CHECKLIST_READ_HOSTS = new Set([
]);

// TWO KEYS to turn a shop on, deliberately. A checklist read must NEVER go live
// before the WRITES move to Supabase: until then the backfill is a frozen
// snapshot, so a flipped shop would show a checklist stuck on import day while
// staff keep ticking into the sheet — they'd re-tick, and presence would break.
// So listing a host is not enough on its own: CHECKLIST_WRITES_MIGRATED must
// ALSO be flipped true, which only happens once ticks actually dual-write to
// Supabase. This fails SAFE — while writes haven't moved, every shop reads the
// sheet exactly like today — and it NEVER throws (a throw on a kiosk could blank
// the screen); it simply stays on the sheet.
const CHECKLIST_WRITES_MIGRATED = false;

// Pure, so the two-key rule can be unit-tested without a browser.
export function checklistReadAllowed(hostname, { hosts = CHECKLIST_READ_HOSTS, writesMigrated = CHECKLIST_WRITES_MIGRATED } = {}) {
  if (!writesMigrated) return false;                  // writes haven't moved — never read Supabase yet
  return hosts.has(String(hostname || "").toLowerCase());
}

// A per-tablet override so Adam can try ONE tablet before listing a host, exactly
// like eodInsights.js (?eodinsightsread) and stockCatalog.js (?catalogread). `on`
// reads Supabase on THIS tablet regardless of the two-key rule — a manual soak: the
// tick still dual-writes to the sheet (which stays the source of truth) and the
// parity gate below still protects the write-back, so it is safe to try one tablet
// after a real sweep. `off` forces the sheet even on a listed host (a one-tap revert
// without a redeploy). Pure (takes the query string), so it is unit-tested; anything
// other than on/off returns null and the host rule decides. Byte-identical to before
// when the param is absent.
export function checklistReadOverride(search) {
  try {
    const q = new URLSearchParams(search || "").get("checklistread");
    if (q === "on") return "on";
    if (q === "off") return "off";
  } catch (e) {}
  return null;
}

export function checklistReadEnabled() {
  if (typeof window === "undefined") return false;
  const o = checklistReadOverride(window.location.search);
  if (o === "on") return true;
  if (o === "off") return false;
  return checklistReadAllowed(window.location.hostname);
}

// --- the CONFIG AUTHORITATIVE gate (Lane BV, 13 Sep 2026) -----------------------
// EMPTY on purpose, like CHECKLIST_READ_HOSTS. A THIRD switch, for the CONFIG read only
// (the sections/tasks/sweeps the form is built from — NOT the day's ticks, and NOT the
// tick write, which is CHECKLIST_WRITE_HOSTS below). Today a flipped shop is served the
// Supabase config only when it matches the live Sheet config (the form-seed drift guard
// in fetchChecklistConfig), re-fetching the Sheet to check every time. That guard is
// right while the Command Centre still writes the Sheet. Once the CC config editor goes
// door-only (admin #207, Sheet write OFF), the first edit makes Supabase differ from the
// Sheet and every flipped shop would fall back to the STALE Sheet — the edit never
// reaching the form. Listing a host here (or ?checklistconfigauth=on on one tablet) makes
// fetchChecklistConfig serve the Supabase config WITHOUT fetching or comparing the Sheet:
// no ?action=checklistconfig call at all. If Supabase fails in this mode it returns null
// and the screen keeps its last-known config (the existing path for a Sheet it cannot
// reach) — never a silent fallback to a stale Sheet.
// Precondition enforced in code (configAuthoritative.js): the shop must ALREADY read
// Supabase (CHECKLIST_READ_HOSTS + CHECKLIST_WRITES_MIGRATED, or ?checklistread=on); a
// listed host whose read isn't on stays guarded and warns once. ?checklistconfigauth=off
// always wins. Order per shop: read flip → soak → (write-authoritative, §5) → THIS →
// then the CC Sheet-write flag (SWITCHON_CHECKLIST.md).
const CHECKLIST_CONFIG_AUTHORITATIVE_HOSTS = new Set([
  // "trafford.eatrolled.com",   // <- added ONLY at the config authoritative step, AFTER the shop's read has flipped + soaked
]);
export function checklistConfigAuthoritativeEnabled() {
  return configAuthoritativeEnabled({
    hosts: CHECKLIST_CONFIG_AUTHORITATIVE_HOSTS, param: "checklistconfigauth",
    readEnabled: checklistReadEnabled(),
    label: "checklist", readGate: "CHECKLIST_READ_HOSTS / ?checklistread",
  });
}

// --- the WRITE gate (Lane BD) ------------------------------------------------
// EMPTY on purpose, exactly like CHECKLIST_READ_HOSTS above. This is the SECOND,
// separate switch and it decides where a checklist tick / RAK is actually
// RECORDED. Today (empty set) every shop keeps writing the Google Sheet as the
// authority, and Supabase is only the best-effort shadow (checklistSync.js). Add a
// hostname here — e.g. "trafford.eatrolled.com" — to make Supabase the AWAITED,
// CONFIRMED record for that shop and STOP posting the Sheet. Nothing here fires
// until a host is listed (or a per-tablet ?checklistwrite=on try-first), so merge
// day is byte-for-byte today for every shop.
const CHECKLIST_WRITE_HOSTS = new Set([
]);

// A per-tablet override, the write twin of checklistReadOverride: ?checklistwrite=on
// makes THIS tablet write to Supabase authoritatively (a pre-shift soak, run it with
// ?checklistread=on so the confirm re-read has Supabase to read); ?checklistwrite=off
// forces the Sheet even on a listed host (a one-tap revert, no redeploy). Pure, so
// it is unit-tested; anything other than on/off returns null and the host rule
// decides. `off` ALWAYS wins.
export function checklistWriteOverride(search) {
  try {
    const q = new URLSearchParams(search || "").get("checklistwrite");
    if (q === "on") return "on";
    if (q === "off") return "off";
  } catch (e) {}
  return null;
}

// Warn-once bookkeeping so a mis-listed host (write set, but read still on the Sheet)
// logs a single console line, not one per tick.
const _warnedWriteWithoutRead = new Set();

// Pure, so the rule can be unit-tested without a browser. A checklist WRITE is
// authoritative for a host ONLY when BOTH hold:
//   1. the host is listed in CHECKLIST_WRITE_HOSTS (or ?checklistwrite=on on this
//      tablet), AND
//   2. the checklist READ is enabled for that host (readEnabled).
// The read guard is NOT optional: the confirm-by-re-read AND the on-screen tick list
// read Supabase only when the READ is flipped, so an authoritative write against a
// Sheet-reading screen would file a tick the screen can't see. If a host is set for
// writes but its read isn't on, we REFUSE (return false), warn ONCE, and stay on the
// safe dual-write path (Sheet still authoritative) — this never throws (a throw on a
// kiosk could blank the screen). `?checklistwrite=off` always wins, so a single
// tablet can be pulled straight back. `readEnabled` defaults to checklistReadEnabled()
// (which is false under Node), so a caller passes it in for the pure/test path.
export function checklistWriteAuthoritative(hostname, search, { writeHosts = CHECKLIST_WRITE_HOSTS, readEnabled = checklistReadEnabled() } = {}) {
  const override = checklistWriteOverride(search);
  if (override === "off") return false;                 // off always wins
  const host = String(hostname || "").toLowerCase();
  const wants = override === "on" || writeHosts.has(host);
  if (!wants) return false;
  if (!readEnabled) {
    if (!_warnedWriteWithoutRead.has(host)) {
      _warnedWriteWithoutRead.add(host);
      try { console.warn("[checklist] write-authoritative is set for", host || "(this tablet)", "but the checklist READ is not enabled — staying on the dual-write path (Sheet still authoritative). Flip the READ for this host first."); } catch (e) {}
    }
    return false;
  }
  return true;
}

// The London date (YYYY-MM-DD) — a tick at 00:30 belongs to the new day here,
// not to yesterday's UTC date.
export function londonToday() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
}

// A clock-skew allowance so a kiosk whose clock runs fast doesn't fail to
// recognise its OWN just-written row (the optimistic ts is stamped on the tablet,
// the server row's ts on Apps Script). The guard is one-directional — it only
// wrongly excludes a real row if the tablet clock is MORE than this ahead of the
// server — so it is set generously (5 min) to make a false "didn't save" during a
// brownout effectively impossible. The only thing this trades away is rejecting a
// stale pre-undo row on an undo→re-tick that happens within the window, a far
// rarer path than a mis-set kiosk clock.
export const SLOT_SKEW_MS = 300000;

// Does this day's events list contain a LANDED (non-undo) tick for `slot`
// {tierOrder, section, task}? Used to (a) confirm a checklist tick really saved
// after an uncertain reply, and (b) tell a still-in-flight optimistic tick apart
// from a phantom the poll should drop. When `sinceMs` is given, the matching row
// must be at/after it (minus SLOT_SKEW_MS) so a STALE row — e.g. a pre-undo tick
// on a slot that was undone and is being re-ticked — can't masquerade as this
// tick's confirmation. A row with no parseable ts falls back to a plain slot
// match, so a missing timestamp can never cause a real match to be missed.
export function slotIsPresent(events, slot, sinceMs = 0, skewMs = SLOT_SKEW_MS) {
  if (!Array.isArray(events) || !slot) return false;
  const cutoff = sinceMs ? sinceMs - skewMs : 0;
  return events.some((e) => {
    if (!e || e.action === "undo") return false;
    if (Number(e.tierOrder) !== Number(slot.tierOrder)) return false;
    if (e.section !== slot.section || e.task !== slot.task) return false;
    if (!cutoff) return true;
    const t = e.ts != null ? new Date(e.ts).getTime() : NaN;
    return Number.isNaN(t) ? true : t >= cutoff;
  });
}

// A config is only usable if it actually has something to tick.
//
// The hole this closes: "no sections and no tiers" is the correct answer for a
// shop that's deliberately switched off (the kitchen), AND the answer you get
// for a shop whose config was never imported. Believing the second one shows
// real staff an empty checklist and looks like it was turned off. Supabase says
// which it is via `configured`; the sheet never claims to be unconfigured, so a
// sheet answer is taken at face value as it always was.
export function configLooksUsable(cfg) {
  if (!cfg || typeof cfg !== "object" || cfg.error) return false;
  const sections = Array.isArray(cfg.sections) ? cfg.sections : [];
  const tiers = Array.isArray(cfg.tiers) ? cfg.tiers : [];
  if (cfg.configured === false) return false;         // no row for this shop — don't trust it
  if (cfg.visibleToStaff === false) return true;      // deliberately switched off — that IS the config
  return sections.length > 0 && tiers.length > 0;
}

// --- form-seed drift guard ---------------------------------------------------
// THE FORM-SEED DEFECT this closes: a checklist tick is written BACK into the
// Google Sheet by NAME (foh.jsx posts section.name + task.name + tier.name). If the
// tick FORM is built from the Supabase config — a frozen one-shot July import — while
// the real config keeps being edited on the Google side, a tick lands under a STALE
// name the sheet no longer recognises, quietly corrupting the record. So the form may
// only come from Supabase when Supabase MATCHES the sheet's live config; any drift and
// the whole session falls back to the sheet (see fetchChecklistConfig).
//
// The action string every checklist event carries. Both the sheet feed
// (getChecklistDay emits the stored action column, which is this routing literal for
// every live tick) and the Supabase reader (checklist_day, 0051) emit exactly this,
// so the client's `action === "undo"` skip stays consistently inert on BOTH sources —
// there is no live "undo" action wired anywhere today. Pinned by a test so a future
// change can't let the two sources diverge. (A genuine undo feature — a write door,
// an undo `action` in both readers, and a kiosk control — is a separate build; specced
// in REMOVAL_PACK_CHECKLIST_WRITES.md, out of scope for the read flip.)
export const CHECKLIST_TICK_ACTION = "checklistTick";

// A comparison-only signature of a config: ONLY the fields that build the tick form
// AND get written back to the sheet — tier order+name, section order+name, each task's
// order+name, and visibleToStaff. Icons are cosmetic and never posted, so they are
// ignored (an icon change must not force a fallback). Names are compared EXACTLY
// (trimmed): the write-back posts the name verbatim, so "Wipe" vs "Wipe " or a case
// change is a real drift that would file a tick under a name the sheet doesn't have.
export function checklistConfigSignature(cfg) {
  const c = cfg && typeof cfg === "object" ? cfg : {};
  const byOrderName = (a, b) => (Number(a.order) - Number(b.order)) || a.name.localeCompare(b.name);
  const tiers = (Array.isArray(c.tiers) ? c.tiers : [])
    .map((t) => ({ order: Number(t && t.order), name: String((t && t.name) ?? "").trim() }))
    .sort(byOrderName);
  const sections = (Array.isArray(c.sections) ? c.sections : [])
    .map((s) => ({
      order: Number(s && s.order),
      name: String((s && s.name) ?? "").trim(),
      tasks: (Array.isArray(s && s.tasks) ? s.tasks : [])
        .map((t) => ({ order: Number(t && t.order), name: String((t && t.name) ?? "").trim() }))
        .sort(byOrderName),
    }))
    .sort(byOrderName);
  return { visible: !!c.visibleToStaff, tiers, sections };
}

// Flatten a signature into stable one-line facts, so a diff reads in plain English.
function flattenSignature(sig) {
  const lines = [`visibleToStaff=${sig.visible}`];
  for (const t of sig.tiers) lines.push(`tier ${t.order}: ${t.name}`);
  for (const s of sig.sections) {
    lines.push(`section ${s.order}: ${s.name}`);
    for (const tk of s.tasks) lines.push(`task [${s.name}] ${tk.order}: ${tk.name}`);
  }
  return lines;
}

// Compare the sheet config (the source of truth for names) against the Supabase config
// (the candidate form). Pure → { clean, drift:[...] }. `clean` is true only when every
// tier/section/task name+order and the visibility flag match exactly. Each drift line
// names the difference in plain English (a rename shows as two lines — one side has the
// old name, the other the new). Used by fetchChecklistConfig to decide, per session,
// whether the Supabase form is safe to serve.
export function checklistConfigParity(sheetCfg, sbCfg) {
  const sheet = flattenSignature(checklistConfigSignature(sheetCfg));
  const supa = flattenSignature(checklistConfigSignature(sbCfg));
  const sheetSet = new Set(sheet);
  const supaSet = new Set(supa);
  const drift = [];
  for (const line of supa) if (!sheetSet.has(line)) drift.push(`supabase has, sheet lacks → ${line}`);
  for (const line of sheet) if (!supaSet.has(line)) drift.push(`sheet has, supabase lacks → ${line}`);
  return { clean: drift.length === 0, drift };
}

// --- the sections, tasks and sweeps for one shop -----------------------------
// Returns { config, _source: "supabase" | "sheet", _parity, _drift? } or null if both
// failed. On a flipped shop the Supabase config is served ONLY when it matches the
// sheet's live config (the form-seed drift guard above); any drift — or a sheet we
// cannot reach to verify — falls the WHOLE session back to the sheet, so a tick can
// never write a stale name. When OFF (the default) this is byte-for-byte the old sheet
// fetch: no rpc call and no parity fetch.
//   _parity: "clean"  the Supabase form was verified equal to the live sheet config
//            "drift"   a mismatch was found; the sheet is being served instead
//            "authoritative"  Supabase served WITHOUT a sheet check (Lane BV, the host is
//                      on CHECKLIST_CONFIG_AUTHORITATIVE_HOSTS / ?checklistconfigauth=on)
//            null      the sheet path (OFF, or a plain Supabase failure) — as before
// `authoritative` defaults to the runtime gate (false everywhere today); injectable so the
// unit test can prove the sheet is never fetched in that mode.
export async function fetchChecklistConfig({
  sheetUrl, site,
  enabled = checklistReadEnabled(),
  authoritative = checklistConfigAuthoritativeEnabled(),
  rpc = (n, a) => supabase.rpc(n, a),
  httpGet = (u) => fetch(u),
  retry = {},
} = {}) {
  // One place that fetches the sheet config with the retry-until-JSON helper — used
  // both as the fallback source AND as the parity yardstick, so the two can't diverge.
  const getSheetConfig = async () => {
    if (!sheetUrl) return null;
    const d = await fetchSheetJson(httpGet, sheetUrl, retry);
    return (d && !d.error) ? d : null;
  };
  const sheetResult = async (why) => {
    if (why) { try { console.warn("[checklist] config fell back to the sheet:", why); } catch (e) {} }
    const d = await getSheetConfig();
    return d ? { config: d, _source: "sheet", _parity: null } : null;
  };

  if (!enabled) return sheetResult(null);
  if (authoritative === true) {
    // AUTHORITATIVE (Lane BV): Supabase IS the config; the sheet is neither fetched nor
    // compared (httpGet is never called). Any failure -> null so the screen keeps its
    // last-known config — never the sheet, which may be stale in this mode.
    const keepCache = (why) => {
      try { console.warn("[checklist] config: AUTHORITATIVE Supabase read failed — keeping the last-known config (no sheet fallback in this mode):", why); } catch (e) {}
      return null;
    };
    try {
      const { data, error } = await rpc("checklist_config_feed", { p_site: site });
      if (error) return keepCache(error.message || "supabase read failed");
      if (!configLooksUsable(data)) return keepCache("supabase returned a checklist with nothing in it");
      return { config: data, _source: "supabase", _parity: "authoritative" };
    } catch (e) {
      return keepCache((e && e.message) || "supabase read threw");
    }
  }
  try {
    const { data, error } = await rpc("checklist_config_feed", { p_site: site });
    if (error) return sheetResult(error.message || "supabase read failed");
    if (!configLooksUsable(data)) return sheetResult("supabase returned a checklist with nothing in it");
    // Supabase is usable — but the tick WRITES BACK to the sheet by name, so the form
    // may only come from Supabase when it MATCHES the sheet's live config. Fetch the
    // sheet config and compare (the form-seed drift guard).
    const sheetCfg = await getSheetConfig();
    if (!sheetCfg) {
      // Can't reach the sheet to verify parity → do NOT trust a possibly-stale Supabase
      // form. Return null so the screen keeps its last-known (verified) cache; if the
      // sheet backend really is down, ticking is broken anyway (the tick posts to it).
      try { console.warn("[checklist] config: sheet unreachable for the parity check — keeping the last-known config"); } catch (e) {}
      return null;
    }
    const parity = checklistConfigParity(sheetCfg, data);
    if (!parity.clean) {
      try { console.warn("[checklist] config: Supabase/Sheet drift — serving the sheet for this session:", parity.drift); } catch (e) {}
      return { config: sheetCfg, _source: "sheet", _parity: "drift", _drift: parity.drift };
    }
    return { config: data, _source: "supabase", _parity: "clean" };
  } catch (e) {
    return sheetResult((e && e.message) || "supabase read threw");
  }
}

// --- one shop's day: ticks, random act, self-reported reviews ----------------
// An EMPTY day is a valid answer here (nobody has started yet), so it is NOT
// treated as a failure — unlike the config above.
// Returns { day, _source } or null if both failed.
export async function fetchChecklistDay({
  sheetUrl, site, date = londonToday(),
  enabled = checklistReadEnabled(),
  rpc = (n, a) => supabase.rpc(n, a),
  httpGet = (u) => fetch(u),
  ensureSession = ensureAnonSession,
  retry = {},
} = {}) {
  const fromSheet = async (why) => {
    if (why) { try { console.warn("[checklist] day fell back to the sheet:", why); } catch (e) {} }
    if (!sheetUrl) return null;
    const d = await fetchSheetJson(httpGet, sheetUrl, retry);
    if (!d || d.error || !Array.isArray(d.events)) return null;
    return { day: d, _source: "sheet" };
  };

  if (!enabled) return fromSheet(null);
  try {
    // The tick log carries staff names, so it needs a signed-in session; a
    // signed-out read would come back empty and look like "nobody has ticked
    // anything", which is exactly the wrong answer.
    await ensureSession();
    const { data, error } = await rpc("checklist_day", { p_site: site, p_date: date });
    if (error) return fromSheet(error.message || "supabase read failed");
    if (!data || !Array.isArray(data.events)) return fromSheet("supabase returned no day");
    // The trap: without a session the tick log reads as EMPTY, which looks
    // exactly like "nobody has ticked anything yet" — a normal state first thing
    // in the morning. The database tells us which it is; trust that, not the
    // empty list.
    if (data.signedIn === false) return fromSheet("the tablet wasn't signed in, so the day would have read as empty");
    return { day: data, _source: "supabase" };
  } catch (e) {
    return fromSheet((e && e.message) || "supabase read threw");
  }
}

// --- ONE day read at a time per shop (login-time reads, 20 Sep 2026) ---------
// PLAIN ENGLISH. Three screens read the same thing — "today's ticks for this
// shop": the login pre-warm, the checklist tile (on open, then every 15 s) and
// the End of Day block. Each used to fire its own read, and the tile's 15 s timer
// fired a NEW one whether or not the last had come back. The read itself is slow
// on the Sheet (the backend walks the whole tick log: 12 s alone on Trafford,
// 24–83 s while other reads were running — measured 20 Sep 2026), so on a busy
// tablet three or four copies of the same slow read ran at once, and every other
// backend call — sell-out, stock, EOD — queued behind them.
//
// This wrapper makes callers SHARE one in-flight read per shop-and-day: whoever
// asks while a read is running is handed that read's answer. Nothing else
// changes — the answer, the fallback rules and the retry are fetchChecklistDay's.
//   fresh:true   start a NEW read even if one is running. Used straight after a
//                write, so a read that began BEFORE the tick can never be taken
//                as its confirmation. Later callers join the new read.
// A read is shared only while it is in flight; once it settles, the next call
// starts a new one. This is single-flight, NOT a cache — freshness across
// screens is the tablet's cached copy's job (readFreshness.js).
// Keyed by shop, London day AND the feed URL, so a read started at 23:59 is never
// handed to a caller asking at 00:01, and two callers only share a read when they
// would have fetched the very same thing. Any other option (enabled, httpGet,
// retry, ...) is taken from the FIRST caller of a shared read — today every
// caller passes none. `read` is injectable for the unit test only.
const _dayReadInflight = new Map();   // "site|date|sheetUrl" -> Promise
export function _resetSharedDayReads() { _dayReadInflight.clear(); }

export function fetchChecklistDayShared({ sheetUrl, site, date = londonToday(), fresh = false, read = fetchChecklistDay, ...rest } = {}) {
  const key = String(site || "") + "|" + String(date || "") + "|" + String(sheetUrl || "");
  if (!fresh) {
    const running = _dayReadInflight.get(key);
    if (running) return running;
  }
  // The read starts NOW (callers must be able to see it in flight at once); a
  // read that throws synchronously becomes a rejection, never a half-registered
  // entry.
  let p;
  try { p = Promise.resolve(read({ sheetUrl, site, date, ...rest })); }
  catch (e) { p = Promise.reject(e); }
  const tracked = p.finally(() => {
    // Only THIS read clears its slot — a `fresh` read that replaced it must stay.
    if (_dayReadInflight.get(key) === tracked) _dayReadInflight.delete(key);
  });
  _dayReadInflight.set(key, tracked);
  return tracked;
}

// Can anyone on this tablet open the checklist tile right now? The home grid
// shows it when the config says visibleToStaff (or the viewer is an Admin) AND
// there is something to tick. The login pre-warm uses this to decide whether the
// day read is worth firing at all: a hidden tile, or a config that hasn't arrived
// yet, gets no read. Pure.
export function checklistTileShowable(cfg, { isAdmin = false } = {}) {
  if (!cfg || typeof cfg !== "object") return false;
  const tiers = Array.isArray(cfg.tiers) ? cfg.tiers : [];
  const sections = Array.isArray(cfg.sections) ? cfg.sections : [];
  if (!tiers.length || !sections.length) return false;
  return !!cfg.visibleToStaff || !!isAdmin;
}
